//== pure calculation engine for the FHS service charges calculator
//== no DOM dependencies; importable from a Node test script
//== source of truth: docs/service-charges-calculator/source-material/
//== "Service Charge Calculator Final.xlsx", worksheet "Calculator".
//==
//== the workbook's formulas, per band row:
//==   currentEquityValue = D4 * D7          //== purchase price x current share
//==   perYear            = currentEquityValue * rate
//==   perMonth           = perYear / 12     //== even twelfth, not a day count
//==
//== D6 (original equity share) is display-only in the sheet - no formula
//== consumes it. we replicate that (plan doc §2, deferred Q3).

//== rate bands, hard-coded in the workbook at C16:C19. defaults only - the
//== rates change over time, so production reads them from config and these
//== exist as a safety net (same pattern as redemption's scheme defaults).
//== rates are decimals, matching the workbook's stored cell values and
//== redemption's scheme convention (0.0175, not 1.75).
export const DEFAULT_BANDS = Object.freeze([
  Object.freeze({ label: 'Years 0 - 5', rate: 0 }),
  Object.freeze({ label: 'Years 6 - 15', rate: 0.0175 }),
  Object.freeze({ label: 'Years 16 - 29', rate: 0.0215 }),
  Object.freeze({ label: 'Years 30+', rate: 0.0285 }),
])

const MONTHS_PER_YEAR = 12

//== converts the user-facing percentage form (e.g. 7.5 for 7.5%) to a decimal
const pctToDecimal = (pct) => pct / 100

const isPositiveFinite = (n) => typeof n === 'number' && Number.isFinite(n) && n > 0
const isNonNegativeFinite = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0

//== the original FHS equity share as a decimal (e.g. 0.10), from the two money
//== inputs alone so it can be displayed before the current share is entered.
//== workbook cell D6 (=+D5/D4). returns null when either input is missing or
//== the denominator is non-positive.
export function originalEquitySharePct(inputs) {
  const oe = inputs?.originalEquity
  const pp = inputs?.purchasePrice
  if (!isNonNegativeFinite(oe) || !isPositiveFinite(pp)) return null
  return oe / pp
}

//== the current equity share to calculate with, in percentage form.
//==
//== the workbook disagrees with itself here: note B21 says percentages are
//== "rounded to two decimal places for the purpose of calculating", but the
//== formulas use D7 at full stored precision. its own worked example uses
//== 7.5%, already 2dp, so the sheet cannot settle it. client Q1 is outstanding
//== (plan doc §6), so we replicate the formulas - full precision - and keep
//== the alternative behind one config flag so the answer is a cheap flip.
export function resolveCurrentEquityPct(inputs, config) {
  const pct = inputs?.currentEquityPct
  if (!isNonNegativeFinite(pct)) return null
  if (!config?.roundCurrentEquityPct) return pct
  return Math.round(pct * 100) / 100
}

//== the four-band table plus the figures behind it. returns null until the
//== inputs the calculation actually needs are present - purchase price and
//== current share. original equity is display-only, so its absence leaves
//== originalEquityPct null rather than blocking the table (validate.js is
//== what requires the customer to fill all three).
export function calculate({ inputs, config } = {}) {
  if (!inputs) return null

  const purchasePrice = inputs.purchasePrice
  const currentEquityPct = resolveCurrentEquityPct(inputs, config)
  if (!isPositiveFinite(purchasePrice) || !isNonNegativeFinite(currentEquityPct)) return null

  const bands = config?.bands?.length ? config.bands : DEFAULT_BANDS
  const currentEquityValue = purchasePrice * pctToDecimal(currentEquityPct)

  const rows = bands.map((band) => {
    const rate = isNonNegativeFinite(band?.rate) ? band.rate : 0
    const perYear = currentEquityValue * rate
    return {
      label: band?.label ?? '',
      rate,
      perYear,
      perMonth: perYear / MONTHS_PER_YEAR,
    }
  })

  return {
    originalEquityPct: originalEquitySharePct(inputs),
    currentEquityPct: pctToDecimal(currentEquityPct),
    currentEquityValue,
    rows,
  }
}
